import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useState,
} from "react";
import api from "../services/apiService";

const OrderContext = createContext();

// Load state from localStorage
const loadState = () => {
  try {
    const savedState = localStorage.getItem("orderState");
    if (savedState === null) {
      return { ordersByRestaurant: {} };
    }
    return JSON.parse(savedState);
  } catch (err) {
    console.error("Error loading state:", err);
    return { ordersByRestaurant: {} };
  }
};

// Save state to localStorage
const saveState = (state) => {
  try {
    localStorage.setItem("orderState", JSON.stringify(state));
  } catch (err) {
    console.error("Error saving state:", err);
  }
};

const orderReducer = (state, action) => {
  let newState;

  switch (action.type) {
    case "SET_RESTAURANT_ORDERS":
      newState = {
        ...state,
        ordersByRestaurant: {
          ...state.ordersByRestaurant,
          [action.payload.restaurantId]: action.payload.orders,
        },
      };
      saveState(newState);
      return newState;

    case "ADD_ORDER":
      const restaurantOrders =
        state.ordersByRestaurant[action.payload.restaurantId] || [];
      newState = {
        ...state,
        ordersByRestaurant: {
          ...state.ordersByRestaurant,
          [action.payload.restaurantId]: [
            action.payload.order,
            ...restaurantOrders,
          ],
        },
      };
      saveState(newState);
      return newState;

    case "UPDATE_ORDER_STATUS":
      const updatedOrders = (
        state.ordersByRestaurant[action.payload.restaurantId] || []
      ).map((order) =>
        order.id === action.payload.orderId
          ? { ...order, status: action.payload.status }
          : order
      );
      newState = {
        ...state,
        ordersByRestaurant: {
          ...state.ordersByRestaurant,
          [action.payload.restaurantId]: updatedOrders,
        },
      };
      saveState(newState);
      return newState;

    case "REMOVE_ORDER":
      const filteredOrders = (
        state.ordersByRestaurant[action.payload.restaurantId] || []
      ).filter((order) => order.id !== action.payload.orderId);
      newState = {
        ...state,
        ordersByRestaurant: {
          ...state.ordersByRestaurant,
          [action.payload.restaurantId]: filteredOrders,
        },
      };
      saveState(newState);
      return newState;

    default:
      return state;
  }
};

export const OrderProvider = ({ children }) => {
  const [state, dispatch] = useReducer(orderReducer, loadState());
  const [pollingIntervals, setPollingIntervals] = useState({});

  // Fetch all orders for a restaurant
  const fetchRestaurantOrders = async (restaurantId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`orders/restaurant/${restaurantId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data) {
        dispatch({
          type: "SET_RESTAURANT_ORDERS",
          payload: { restaurantId, orders: response.data },
        });
      }
      return response.data;
    } catch (error) {
      console.error("Error fetching orders:", error);
      throw error;
    }
  };

  // Create a new order
  const createOrder = async (restaurantId, orderData) => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.post(
        `/orders/create/${restaurantId}`,
        orderData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data) {
        // After creating the order, fetch all orders to update the state
        await fetchRestaurantOrders(restaurantId);
        return response.data;
      }
    } catch (error) {
      console.error("Error creating order:", error);
      throw error;
    }
  };

  // Update order status
  const updateOrderStatus = async (restaurantId, orderId, status) => {
    try {
      // Optimistically update the state
      dispatch({
        type: "UPDATE_ORDER_STATUS",
        payload: { restaurantId, orderId, status },
      });

      const token = localStorage.getItem("token");
      await api.put(
        `orders/status/${orderId}?status=${status}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Fetch fresh data to ensure consistency
      await fetchRestaurantOrders(restaurantId);
    } catch (error) {
      console.error("Error updating order status:", error);
      // Revert the optimistic update on error
      const currentOrder = getOrderById(restaurantId, orderId);
      if (currentOrder) {
        dispatch({
          type: "UPDATE_ORDER_STATUS",
          payload: { restaurantId, orderId, status: currentOrder.status },
        });
      }
      throw error;
    }
  };

  const removeOrder = async (restaurantId, orderId) => {
    try {
      const token = localStorage.getItem("token");
      await api.delete(`orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      dispatch({
        type: "REMOVE_ORDER",
        payload: { restaurantId, orderId },
      });
    } catch (error) {
      console.error("Error removing order:", error);
      throw error;
    }
  };

  const getRestaurantOrders = (restaurantId) => {
    return state.ordersByRestaurant[restaurantId] || [];
  };

  const getOrdersByStatus = (restaurantId, status) => {
    const restaurantOrders = state.ordersByRestaurant[restaurantId] || [];
    return restaurantOrders.filter((order) => order.status === status);
  };

  const getOrderById = (restaurantId, orderId) => {
    const restaurantOrders = state.ordersByRestaurant[restaurantId] || [];
    return restaurantOrders.find((order) => order.id === orderId);
  };

  const startPolling = (restaurantId) => {
    // Clear any existing polling interval for this restaurant
    if (pollingIntervals[restaurantId]) {
      clearInterval(pollingIntervals[restaurantId]);
    }

    // Start new polling interval
    const intervalId = setInterval(() => {
      fetchRestaurantOrders(restaurantId);
    }, 30000); // Poll every 30 seconds

    setPollingIntervals((prev) => ({
      ...prev,
      [restaurantId]: intervalId,
    }));
  };

  const stopPolling = (restaurantId) => {
    if (pollingIntervals[restaurantId]) {
      clearInterval(pollingIntervals[restaurantId]);
      setPollingIntervals((prev) => {
        const newIntervals = { ...prev };
        delete newIntervals[restaurantId];
        return newIntervals;
      });
    }
  };

  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(pollingIntervals).forEach((interval) =>
        clearInterval(interval)
      );
    };
  }, []);

  const value = {
    getRestaurantOrders,
    fetchRestaurantOrders,
    createOrder,
    updateOrderStatus,
    removeOrder,
    getOrdersByStatus,
    getOrderById,
    startPolling,
    stopPolling,
  };

  return (
    <OrderContext.Provider value={value}>{children}</OrderContext.Provider>
  );
};

export const useOrder = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error("useOrder must be used within an OrderProvider");
  }
  return context;
};
